import { useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Banner from "@/components/Banner";
import { useMutation, gql, TypedDocumentNode, DocumentNode } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";

const DELETE_SUSPENSION_MUTATION: TypedDocumentNode<
  {
    deleteSuspension: {
      success: boolean;
      message: string;
    };
  },
  {
    id: string;
  }
> = gql`
  mutation DeleteSuspension($id: ID!) {
    deleteSuspension(id: $id) {
      success
      message
    }
  }
`;

interface DeleteSuspensionModalProps {
  open: boolean;
  onClose: () => void;
  suspensionId: string;
  refetchQueries?: DocumentNode[];
  onSuspensionMutationSuccess: () => Promise<void> | void;
}

export default function DeleteSuspensionModal({
  open,
  onClose,
  suspensionId,
  refetchQueries,
  onSuspensionMutationSuccess
}: DeleteSuspensionModalProps) {
  const [bannerError, setBannerError] = useState("");

  const [deleteSuspension, { loading }] = useMutation(DELETE_SUSPENSION_MUTATION, {
    refetchQueries: refetchQueries || [],
    awaitRefetchQueries: true,
    onCompleted: async (data) => {
      if (data.deleteSuspension.success) {
        try {
          await onSuspensionMutationSuccess();
        } finally {
          showSnackbar("Suspension deleted successfully");
          handleClose();
        }
      } else {
        setBannerError(data.deleteSuspension.message);
      }
    },
    onError: (error) => {
      setBannerError(`Error: ${error.message}`);
    }
  });

  const handleClose = () => {
    setBannerError("");
    onClose();
  };

  const handleDelete = async () => {
    await deleteSuspension({
      variables: {
        id: suspensionId
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Delete Suspension Record"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon={loading ? "progress_activity" : "delete"}
            iconClassName={loading ? "animate-spin" : undefined}
            onClick={handleDelete}
            disabled={loading}
            data-testid="confirm-delete-suspension"
          >
            Delete
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The suspension could not be deleted." subText={bannerError} />
        </div>
      )}
      <p>Are you sure you want to delete this suspension record? This cannot be undone.</p>
    </Dialog>
  );
}
